import React from "react";
import { GlassCard } from "../components/GlassCard";
import { NumberInput } from "../components/NumberInput";
import { useBudget } from "../context/BudgetContext";
import { useI18n } from "../context/I18nContext";
import {
  CHARGES_FIXES_FIELDS,
  CREDITS_FIELDS,
  DEPENSES_VARIABLES_FIELDS,
} from "../lib/constants";
import { formatCurrency } from "../lib/i18n";
import { Home, CreditCard, ShoppingBag } from "lucide-react";

const Section = ({ icon: Icon, title, total, totalLabel, fields, prefix, testIdPrefix }) => {
  const { state, setDepenseField } = useBudget();
  const { t, lang } = useI18n();
  return (
    <GlassCard data-testid={`section-${testIdPrefix}`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Icon className="w-5 h-5 text-indigo-950" />
          </div>
          <h2 className="text-lg font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
            {title}
          </h2>
        </div>
        <div className="flex flex-col items-end" data-testid={`subtotal-${testIdPrefix}`}>
          <span className="text-xs uppercase tracking-[0.2em] text-slate-400">{totalLabel}</span>
          <span className="text-2xl font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
            {formatCurrency(total, lang)}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <NumberInput
            key={f}
            testId={`input-${prefix}-${f}`}
            label={t.depenses[f]}
            value={state.depenses[f]}
            onChange={(v) => setDepenseField(f, v)}
          />
        ))}
      </div>
    </GlassCard>
  );
};

export const DepensesModule = () => {
  const { t, lang } = useI18n();
  const { indicators } = useBudget();

  return (
    <div className="flex flex-col gap-8" data-testid="module-depenses">
      <header className="flex flex-col gap-2">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
          {t.modules.depenses}
        </span>
        <h1 className="text-3xl sm:text-4xl tracking-tighter text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          {t.depenses.title}
        </h1>
        <p className="text-slate-500 text-base max-w-2xl">{t.depenses.subtitle}</p>
      </header>

      <Section
        icon={Home}
        title={t.depenses.fixes}
        total={indicators.chargesFixes}
        totalLabel={t.depenses.totalFixes}
        fields={CHARGES_FIXES_FIELDS}
        prefix="fixes"
        testIdPrefix="fixes"
      />

      <Section
        icon={CreditCard}
        title={t.depenses.credits}
        total={indicators.credits}
        totalLabel={t.depenses.totalCredits}
        fields={CREDITS_FIELDS}
        prefix="credits"
        testIdPrefix="credits"
      />

      <Section
        icon={ShoppingBag}
        title={t.depenses.variables}
        total={indicators.variables}
        totalLabel={t.depenses.totalVariables}
        fields={DEPENSES_VARIABLES_FIELDS}
        prefix="variables"
        testIdPrefix="variables"
      />

      <div
        data-testid="total-depenses"
        className="rounded-2xl bg-indigo-950 text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-lg"
      >
        <span className="text-xs uppercase tracking-[0.2em] text-white/60">{t.depenses.totalGeneral}</span>
        <span
          className="text-4xl sm:text-5xl font-medium tracking-tighter text-rose-300"
          style={{ fontFamily: "Outfit, sans-serif" }}
          data-testid="total-depenses-value"
        >
          {formatCurrency(indicators.depensesTotales, lang)}
        </span>
      </div>
    </div>
  );
};
